import {
    Alert,
    Box,
    Button,
    ButtonRow,
    Checkbox,
    Form,
    FormGroup,
    Heading,
    List,
    Select,
    Text,
    TextArea,
} from '@ksyos/design-system';
import { merge } from 'lodash';
import { useEffect, useState } from 'react';
import type { ITfoDiagnosticReportCustomControlData } from '@common/validation/customControls/ophthalmology';
import { getSelectedLanguage } from '@frontend/translate';
import type { IOption } from '../interfaces';
import { TypeaheadInput } from '../fields';
import {
    PolicyAndUrgencyAddEditFields,
    RetinopathieAndMaculopathieFields,
} from '../pages/customControls/TFO/v1/TfoV1DiagnosticReportAddEditCustomControlFields';

export enum AddendumAddEditValueInputType {
    fundusscreeningPolicyAndUrgencyFields,
    retinopathieAndMaculopathieSelector,
    select,
    textArea,
    typeAhead,
}

interface IValueBase {
    checked?: boolean;
    label: string;
    type: AddendumAddEditValueInputType;
}

interface IValueSelect extends IValueBase {
    options: IOption<string>[];
    type: AddendumAddEditValueInputType.select;
    value?: string;
}

interface IValueTypeahead extends IValueBase {
    options: string[];
    type: AddendumAddEditValueInputType.typeAhead;
    value?: string;
}

interface IValueTextarea extends IValueBase {
    maxLength?: number;
    type: AddendumAddEditValueInputType.textArea;
    value?: string;
}

interface IValueRetinopathieAndMaculopathie extends IValueBase {
    customControlData: ITfoDiagnosticReportCustomControlData;
    type: AddendumAddEditValueInputType.retinopathieAndMaculopathieSelector;
    value?: string;
}

export interface IValueFundusscreeningPolicyAndUrgency extends IValueBase {
    customControlData: ITfoDiagnosticReportCustomControlData;
    type: AddendumAddEditValueInputType.fundusscreeningPolicyAndUrgencyFields;
    value?: string;
}

export interface IAddendumAddEditValue {
    key: string;
    value:
        | IValueSelect
        | IValueTypeahead
        | IValueTextarea
        | IValueRetinopathieAndMaculopathie
        | IValueFundusscreeningPolicyAndUrgency;
}

interface IAddendumAddEditProps<TValue extends IAddendumAddEditValue> {
    disableCancel?: boolean;
    disableInput?: boolean;
    disableSend?: boolean;
    onCancel: () => void;
    onChange?: (value: TValue[]) => void;
    onSave: (value: TValue[]) => void;
    value: TValue[];
}

export function AddendumAddEdit<TValue extends IAddendumAddEditValue>(props: IAddendumAddEditProps<TValue>) {
    const translate = getSelectedLanguage();
    const { disableInput, disableSend, disableCancel, onCancel, onSave, onChange } = props;

    const [value, setValue] = useState(structuredClone(props.value));
    const [originalValue] = useState(structuredClone(value));

    useEffect(() => {
        setValue(structuredClone(props.value));
    }, [props.value]);

    return (
        <Form submitOnEnter={false}>
            <Box
                display="flex"
                flexDirection={'column'}
                gap={8}
            >
                <Heading size="medium">{translate.addendum.header}</Heading>
                <Alert
                    appearance="info"
                    style={{ margin: '0' }}
                >
                    {translate.addendum.info}
                </Alert>
                <FormGroup label={translate.addendum.selectEdits}>
                    <List>
                        {value.map(({ key: entryKey, value: entryValue }) => (
                            <List.Item key={entryKey}>
                                <Checkbox
                                    checked={entryValue.checked ?? false}
                                    disabled={disableInput}
                                    label={<Text as="span">{entryValue.label}</Text>}
                                    onChange={(e) =>
                                        handleChange([
                                            {
                                                key: entryKey,
                                                value: {
                                                    checked: e.target.checked,
                                                    value: e.target.checked
                                                        ? entryValue.value
                                                        : originalValue.find((entry) => entry.key === entryKey)?.value
                                                              .value,
                                                },
                                            },
                                        ])
                                    }
                                />
                                {entryValue.checked && (
                                    <Box style={{ marginBottom: '16px', marginLeft: '32px', marginTop: '8px' }}>
                                        {entryValue.type === AddendumAddEditValueInputType.textArea && (
                                            <TextArea
                                                disabled={disableInput}
                                                maxLength={entryValue.maxLength}
                                                onChange={(e) =>
                                                    handleChange([{ key: entryKey, value: { value: e.target.value } }])
                                                }
                                                rows={5}
                                                value={entryValue.value || ''}
                                                variant="outline"
                                            />
                                        )}
                                        {entryValue.type === AddendumAddEditValueInputType.typeAhead && (
                                            <TypeaheadInput
                                                disabled={disableInput}
                                                onChange={(newValue) =>
                                                    handleChange([{ key: entryKey, value: { value: newValue ?? '' } }])
                                                }
                                                options={entryValue.options ?? []}
                                                value={entryValue.value || ''}
                                                variant="outline"
                                            />
                                        )}
                                        {entryValue.type === AddendumAddEditValueInputType.select && (
                                            <Select
                                                isDisabled={disableInput}
                                                onChange={(newValue) =>
                                                    handleChange([{ key: entryKey, value: { value: newValue?.value } }])
                                                }
                                                options={entryValue.options ?? []}
                                                value={entryValue.options?.find(
                                                    (entry) => entry.value === entryValue.value
                                                )}
                                            />
                                        )}
                                        {entryValue.type ===
                                            AddendumAddEditValueInputType.retinopathieAndMaculopathieSelector && (
                                            <RetinopathieAndMaculopathieFields skipStandardInfo={true} />
                                        )}
                                        {entryValue.type ===
                                            AddendumAddEditValueInputType.fundusscreeningPolicyAndUrgencyFields && (
                                            <PolicyAndUrgencyAddEditFields />
                                        )}
                                    </Box>
                                )}
                            </List.Item>
                        ))}
                    </List>
                </FormGroup>
                <ButtonRow justify="right">
                    <Button
                        isDisabled={disableCancel}
                        onClick={handleCancel}
                        variant="outline"
                    >
                        {translate.common.cancel}
                    </Button>
                    <Button
                        isDisabled={disableSend}
                        onClick={() => onSave(value)}
                        variant="solid"
                    >
                        {translate.addendum.send}
                    </Button>
                </ButtonRow>
            </Box>
        </Form>
    );

    function handleCancel() {
        setValue(originalValue);
        onCancel();
    }

    function handleChange(newValue: { key: string; value: Partial<IAddendumAddEditValue['value']> }[]) {
        const responseValue = structuredClone(value).map((currentValue) => {
            newValue.forEach(({ key: entryKey, value: entryValue }) => {
                if (currentValue.key === entryKey) {
                    currentValue.value = merge(currentValue.value, entryValue);
                }
            });

            return currentValue;
        });
        onChange?.(responseValue);
        setValue(responseValue);
    }
}
